import { FC } from 'react';
import { useHistory } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { differenceInYears } from 'date-fns';

import { AccountClosureReason } from '__generated__/GQL';
import mailImg from 'assets/brand/mail.png';
import useCompanyContext from 'common/hooks/useCompanyContext';
import Button from 'components/_core/Button';
import Text from 'components/_core/Text';
import DecorativeImage from 'components/DecorativeImage';
import HelpSection from 'components/HelpSection';
import Loader from 'components/Loader';

import ButtonRow from '../components/ButtonRow';
import Card from '../components/Card';
import Title from '../components/Title';
import { AccountClosureDocument } from './accountClosure.gql';
import { CancelAccountClosureDocument } from './cancelAccountClosure.gql';
import { getPersonalizedTone } from './helpers';
import locales from './locales';

const ClosureCancelation: FC = () => {
  const history = useHistory();

  const { companyProfileId } = useCompanyContext();

  const { data, loading: loadingAccountClosure } = useQuery(
    AccountClosureDocument,
    {
      skip: !companyProfileId,
      variables: { companyProfileId: companyProfileId || '' },
    },
  );

  const accountClosure = data?.accountClosure;
  const companyProfile = data?.viewer.company.profile;

  const [cancelAccountClosure] = useMutation(CancelAccountClosureDocument, {
    variables: {
      input: {
        accountClosureId: accountClosure?.accountClosureId || '',
      },
    },
  });

  if (loadingAccountClosure || !companyProfile || !accountClosure) {
    return (
      <Card>
        <Loader />
      </Card>
    );
  }

  const { reasons } = accountClosure;
  const creationDate = new Date(companyProfile.createdAt);

  const isStoppingActivity = reasons.includes(
    AccountClosureReason.StopActivity,
  );

  const isOlderThanAYear = differenceInYears(creationDate, new Date()) >= 1;

  const { body, title } = getPersonalizedTone({
    isOlderThanAYear,
    isStoppingActivity,
  });

  return (
    <>
      <Card>
        <DecorativeImage src={mailImg} />
        <Title>{title}</Title>
        <Text size="sm">{body}</Text>
        <ButtonRow>
          <Button
            onClick={async () => {
              await cancelAccountClosure();
              history.push('/closure-cancel-confirmation');
            }}
            variant="secondary"
          >
            {locales.cancel}
          </Button>
        </ButtonRow>
      </Card>
      <HelpSection>{locales.help}</HelpSection>
    </>
  );
};

export default ClosureCancelation;
